// marketMonitorLegalSearch.js
import Fuse from 'fuse.js';

// Normaliza texto: minúsculas, sin tildes, sin espacios extra
const normalizeText = (text = '') =>
    text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // quita acentos
        // Quita caracteres invisibles o de formato que rompen las comparaciones literales
        .replace(/[\u200B-\u200D\uFEFF\u2060-\u2063]/g, '')
        // Normaliza variantes de espacios, incluyendo non‑breaking y espaciados finos
        .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
        .toLowerCase()
        .replace(/\s+/g, ' ') // normaliza espacios
        .trim();

const fuseOptions = {
    keys: ['institutionName', '_normalizedName'],
    threshold: 0.35,        // similitud moderada
    includeScore: true,
    minMatchCharLength: 2,
    distance: 500,          // permite similitudes en todo el string
    ignoreLocation: true,   // no penaliza si el match está al medio
    shouldSort: true,
    includeMatches: true,
    findAllMatches: false,
    useExtendedSearch: false,
};

export const createFirmsFuse = (firms) => {
    if (!Array.isArray(firms) || firms.length === 0) return null;

    // Agregamos un campo normalizado para cada firma
    firms.forEach((firm) => {
        if (firm && typeof firm.institutionName === 'string') {
            firm._normalizedName = normalizeText(firm.institutionName);

            // 🔍 Diagnóstico temporal: detectar firmas relacionadas con "ENS"
            const norm = firm._normalizedName;
            if (
                firm.institutionName.toLowerCase().includes('ens') ||
                norm.includes('ens')
            ) {
                console.log('[DEBUG ENS]', {
                    original: `"${firm.institutionName}"`,
                    normalized: `"${norm}"`,
                    length: norm.length,
                    startsWithEns: norm.startsWith('ens'),
                    exactMatch: norm === 'ens',
                    chars: Array.from(norm).map(c => c === ' ' ? '␣' : c.charCodeAt(0) < 32 || c.charCodeAt(0) > 126 ? `U+${c.charCodeAt(0).toString(16).toUpperCase()}` : c),
                });
            }
        } else {
            firm._normalizedName = '';
        }
    });

    return new Fuse(firms, fuseOptions);
};

export const searchFirms = (firmsList, query, fuseInstance) => {
    if (!Array.isArray(firmsList) || firmsList.length === 0) return [];

    const cleanedQuery = normalizeText(query || '');
    if (!cleanedQuery) {
        // Sin query → se muestran todas
        return firmsList;
    }

    // 1) SUBSTRING LITERAL (lo más importante)
    //    Si aquí hay resultados, NO usamos fuzzy.
    const isVeryShortQuery = cleanedQuery.length <= 3;

    const substringMatches = firmsList.filter((firm) => {
        if (!firm || typeof firm.institutionName !== 'string') return false;
        const nameNorm =
            firm._normalizedName || normalizeText(firm.institutionName);
             if (isVeryShortQuery) {
            // Para términos muy cortos, evitamos matches en medio de palabras
            // (ej: "loyENS" se filtra, pero "ENS africa" no).
            const words = nameNorm.split(' ');
            return (
                nameNorm === cleanedQuery ||
                nameNorm.startsWith(cleanedQuery) ||
                words.some((w) => w.startsWith(cleanedQuery))
            );
        }
        return nameNorm.includes(cleanedQuery);
    });

    if (substringMatches.length > 0) {

        substringMatches.sort((a, b) => {
            const aName = a._normalizedName || normalizeText(a.institutionName || '');
            const bName = b._normalizedName || normalizeText(b.institutionName || '');

            const query = cleanedQuery;

            const aWords = aName.split(" ");
            const bWords = bName.split(" ");

            // 0) Máxima prioridad: nombre EXACTO igual a la query
            const aExact = (aName === query);
            const bExact = (bName === query);

            if (aExact && !bExact) return -1;
            if (!aExact && bExact) return 1;

            // 1) Luego: nombres que EMPIEZAN con la query
            const aStarts = aName.startsWith(query);
            const bStarts = bName.startsWith(query);

            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;

            // 2) Luego: la query como palabra completa
            const aHasExactWord = aWords.includes(query);
            const bHasExactWord = bWords.includes(query);

            if (aHasExactWord && !bHasExactWord) return -1;
            if (!aHasExactWord && bHasExactWord) return 1;

            // 3) Luego: posición de la coincidencia
            const aIndex = aName.indexOf(query);
            const bIndex = bName.indexOf(query);

            if (aIndex !== bIndex) return aIndex - bIndex;

            // 4) Último criterio: orden alfabético
            return aName.localeCompare(bName);
        });



        return substringMatches;
    }

    // 2) FALLBACK: Fuzzy search SOLO si no hubo substring matches
    const MIN_QUERY_LENGTH_FOR_FUZZY = 3;
    if (fuseInstance && cleanedQuery.length >= MIN_QUERY_LENGTH_FOR_FUZZY) {
        const fuseResults = fuseInstance.search(cleanedQuery);
        return fuseResults.map((r) => r.item);
    }

    // 3) Si no hay nada de nada, devolvemos lista vacía
    return [];
};

export default searchFirms;
